import React, { useCallback, useRef, useState } from 'react';
import { formatPrice } from '../../../utils/util';
import { useMediaQuery } from 'react-responsive';
import {
  Content,
  ListContainer,
  Navigator,
  PageContainer,
  ListItem,
  Title,
  ListItemColumn,
  ListItemRow,
  MediaCountRow,
  MediaCountText,
  Header,
  AddBundle,
  NavigationContainer,
  LoaderContainer,
  RightSide,
  Filter,
  FilterButton,
  FilterWrapper,
  TitleContainer,
  SyncButton,
} from './VaultPage.styles';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ReactComponent as ArrowIcon } from '../../../assets/icons/chevron_right.svg';
import { ReactComponent as ImageIcon } from '../../../assets/icons/image-04.svg';
import { ReactComponent as VideoRecorderIcon } from '../../../assets/icons/video-recorder.svg';
import { ReactComponent as PlusIcon } from '../../../assets/icons/plus.svg';
import { ReactComponent as DropboxIcon } from '../../../assets/icons/dropbox.svg';
import { Text16Bold, Text16Regular } from '../../../components/utils/Text/Text.styles';
import { useDispatch, useSelector } from 'react-redux';
import { selectFetchMediaBundlesInfo, selectUser } from '../../../store/slices/user/slice';
import { fetchMediaBundles, getDropboxSyncStatus } from '../../../store/slices/user/asyncThunks';
import { useEffect } from 'react';
import { CreateAssetFolderModal, Spinner, UploadBundleModal, UploadMediaFromDropboxModal } from '../../../components';
import { VAULT, VAULT_BUNDLE, VAULT_FOLDER, VAULT_MEDIA } from '../../../components/App/routes';
import { fetchAssetFolders } from '../../../store/slices/assetFolder/asyncThunks';
import { selectFetchAssetFoldersInfo } from '../../../store/slices/assetFolder/slice';
import { selectSelectedInfluencer } from '../../../store/slices/chatter/slice';
import { CRM_ORGANIZATION_MEMBER_ROLE } from '../../../utils/constants';

const TABS = {
  FOLDERS: 'FOLDERS',
  BUNDLES: 'BUNDLES',
};

const VaultPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const uploadBundleModalRef = useRef();
  const createAssetFolderModalRef = useRef();
  const uploadMediaFromDropboxModalRef = useRef();
  const user = useSelector(selectUser);
  const influencer = useSelector(selectSelectedInfluencer);
  const [showContent, setShowContent] = useState(false);
  const smallScreen = useMediaQuery({ minWidth: 0, maxWidth: 768 });
  const [tab, setTab] = useState(TABS.BUNDLES);
  const { data, pending } = useSelector(selectFetchMediaBundlesInfo);
  const { data: foldersData, pending: foldersPending } = useSelector(selectFetchAssetFoldersInfo);
  const { influencerId } = useParams();

  useEffect(() => {
    dispatch(fetchMediaBundles({ ...(influencerId && { influencerId }) }));
    dispatch(fetchAssetFolders({ ...(influencerId && { influencerId }) }));
  }, [dispatch, influencerId]);

  useEffect(() => {
    setShowContent(!smallScreen);
  }, [smallScreen]);

  const [syncIsRunning, setSyncIsRunning] = useState(false);
  const syncPollingRef = useRef(null);

  const stopSyncStatusPolling = useCallback(() => {
    if (syncPollingRef.current) {
      clearInterval(syncPollingRef.current);
      syncPollingRef.current = null;
    }
  }, []);

  const startSyncStatusPolling = useCallback(() => {
    stopSyncStatusPolling();

    const checkStatus = () => {
      dispatch(getDropboxSyncStatus({ influencerId }))
        .unwrap()
        .then(({ isRunning }) => {
          setSyncIsRunning(isRunning);

          if (!isRunning) {
            stopSyncStatusPolling();
          }
        })
        .catch(() => {
          stopSyncStatusPolling();
        });
    };

    checkStatus();
    syncPollingRef.current = setInterval(checkStatus, 2000);
  }, [dispatch, influencerId, stopSyncStatusPolling]);

  useEffect(() => {
    dispatch(getDropboxSyncStatus({ influencerId }))
      .unwrap()
      .then(({ isRunning }) => {
        setSyncIsRunning(isRunning);

        if (isRunning) {
          startSyncStatusPolling();
        }
      })
      .catch(() => {
        setSyncIsRunning(false);
      });

    return () => {
      stopSyncStatusPolling();
    };
  }, [dispatch, influencerId, startSyncStatusPolling, stopSyncStatusPolling]);

  return (
    <PageContainer>
      {(!smallScreen || (smallScreen && !showContent)) && (
        <Navigator>
          <TitleContainer>
            <Title>Vault</Title>
            {[
              CRM_ORGANIZATION_MEMBER_ROLE.OWNER,
              CRM_ORGANIZATION_MEMBER_ROLE.ADMINISTRATOR,
              CRM_ORGANIZATION_MEMBER_ROLE.TEAM_LEAD,
              CRM_ORGANIZATION_MEMBER_ROLE.CONTENT_MANAGER,
            ].includes(user.role) && (
              <SyncButton onClick={() => uploadMediaFromDropboxModalRef.current.show()} disabled={syncIsRunning}>
                Sync <DropboxIcon color="#0061FE" />
              </SyncButton>
            )}
          </TitleContainer>
          <NavigationContainer>
            <ListItem
              $selected={[
                `${influencerId ? `/${influencerId}` : ''}/${VAULT}/${VAULT_MEDIA.replace(':type', 'free')}`,
                `${influencerId ? `/${influencerId}` : ''}/${VAULT}/${VAULT_MEDIA.replace(':type', 'paid')}`,
              ].includes(pathname)}
              onClick={() => {
                setShowContent(true);
                navigate(VAULT_MEDIA.replace(':type', 'paid'));
              }}>
              <ListItemColumn>
                <Text16Regular>All Media</Text16Regular>
                <ListItemRow>
                  <MediaCountRow>
                    <ImageIcon color="#9DA4AE" height={18} width={18} />
                    <MediaCountText>{user.numOfPhotos || influencer?.numOfPhotos}</MediaCountText>
                  </MediaCountRow>
                  <MediaCountRow>
                    <VideoRecorderIcon color="#9DA4AE" height={18} width={18} />
                    <MediaCountText>{user.numOfVideos || influencer?.numOfVideos}</MediaCountText>
                  </MediaCountRow>
                </ListItemRow>
              </ListItemColumn>
              <ArrowIcon color="#1672BA" />
            </ListItem>
            <FilterWrapper>
              <Filter>
                <FilterButton onClick={() => setTab(TABS.BUNDLES)} $selected={tab === TABS.BUNDLES}>
                  Bundles
                </FilterButton>
                <FilterButton onClick={() => setTab(TABS.FOLDERS)} $selected={tab === TABS.FOLDERS}>
                  Folders
                </FilterButton>
              </Filter>
            </FilterWrapper>
            {tab === TABS.FOLDERS && (
              <Header>
                <Text16Bold>Folders</Text16Bold>
                {[
                  CRM_ORGANIZATION_MEMBER_ROLE.OWNER,
                  CRM_ORGANIZATION_MEMBER_ROLE.ADMINISTRATOR,
                  CRM_ORGANIZATION_MEMBER_ROLE.TEAM_LEAD,
                  CRM_ORGANIZATION_MEMBER_ROLE.CONTENT_MANAGER,
                ].includes(user.role) && (
                  <RightSide>
                    <AddBundle onClick={() => createAssetFolderModalRef.current.show()}>
                      <PlusIcon />
                    </AddBundle>
                  </RightSide>
                )}
              </Header>
            )}
            {tab === TABS.FOLDERS &&
              (foldersPending ? (
                <LoaderContainer>
                  <Spinner width={120} height={120} />
                </LoaderContainer>
              ) : (
                <ListContainer>
                  {foldersData.map(folder => {
                    const splittedPathname = pathname.split('/');
                    const isSelected = splittedPathname[splittedPathname.length - 1] === folder.id;
                    return (
                      <ListItem
                        key={folder.id}
                        $selected={isSelected}
                        onClick={() => {
                          setShowContent(true);
                          navigate(VAULT_FOLDER.replace(':folderId', folder.id));
                        }}>
                        <ListItemColumn>
                          <Text16Regular>{folder.name}</Text16Regular>
                          <ListItemRow>
                            <MediaCountRow>
                              <ImageIcon color="#9DA4AE" height={18} width={18} />
                              <MediaCountText>{folder.numOfPhotos || 0}</MediaCountText>
                            </MediaCountRow>
                            <MediaCountRow>
                              <VideoRecorderIcon color="#9DA4AE" height={18} width={18} />
                              <MediaCountText>{folder.numOfVideos || 0}</MediaCountText>
                            </MediaCountRow>
                          </ListItemRow>
                        </ListItemColumn>
                        <ArrowIcon color="#1672BA" />
                      </ListItem>
                    );
                  })}
                </ListContainer>
              ))}
            {tab === TABS.BUNDLES && (
              <Header>
                <Text16Bold>Bundles</Text16Bold>
                {[
                  CRM_ORGANIZATION_MEMBER_ROLE.OWNER,
                  CRM_ORGANIZATION_MEMBER_ROLE.ADMINISTRATOR,
                  CRM_ORGANIZATION_MEMBER_ROLE.TEAM_LEAD,
                  CRM_ORGANIZATION_MEMBER_ROLE.CONTENT_MANAGER,
                ].includes(user.role) && (
                  <RightSide>
                    <AddBundle onClick={() => uploadBundleModalRef.current.show()}>
                      <PlusIcon />
                    </AddBundle>
                  </RightSide>
                )}
              </Header>
            )}
            {tab === TABS.BUNDLES &&
              (pending ? (
                <LoaderContainer>
                  <Spinner width={120} height={120} />
                </LoaderContainer>
              ) : (
                <ListContainer>
                  {data.map(bundle => {
                    const splittedPathname = pathname.split('/');
                    const isSelected = splittedPathname[splittedPathname.length - 1] === bundle.id;
                    return (
                      <ListItem
                        key={bundle.id}
                        $selected={isSelected}
                        onClick={() => {
                          setShowContent(true);
                          navigate(VAULT_BUNDLE.replace(':bundleId', bundle.id));
                        }}>
                        <ListItemColumn>
                          <Text16Regular>{bundle.name}</Text16Regular>
                          <ListItemRow>
                            <MediaCountRow>
                              <ImageIcon color="#9DA4AE" height={18} width={18} />
                              <MediaCountText>{bundle.numOfPhotos}</MediaCountText>
                            </MediaCountRow>
                            <MediaCountRow>
                              <VideoRecorderIcon color="#9DA4AE" height={18} width={18} />
                              <MediaCountText>{bundle.numOfVideos}</MediaCountText>
                            </MediaCountRow>
                            {!!bundle.cost && <MediaCountText>${formatPrice(bundle.cost)}</MediaCountText>}
                          </ListItemRow>
                        </ListItemColumn>
                        <ArrowIcon color="#1672BA" />
                      </ListItem>
                    );
                  })}
                </ListContainer>
              ))}
          </NavigationContainer>
        </Navigator>
      )}
      {showContent && (
        <Content>
          <MemoOutlet context={[setShowContent]} />
        </Content>
      )}
      <UploadBundleModal
        modalRef={uploadBundleModalRef}
        influencerId={influencerId}
        onSuccess={() => {
          dispatch(fetchMediaBundles({ ...(influencerId && { influencerId }) }));
        }}
      />
      <CreateAssetFolderModal modalRef={createAssetFolderModalRef} onSuccess={() => {}} influencerId={influencerId} />
      <UploadMediaFromDropboxModal
        modalRef={uploadMediaFromDropboxModalRef}
        influencerId={influencerId}
        onSyncStarted={startSyncStatusPolling}
      />
    </PageContainer>
  );
};

const MemoOutlet = React.memo(() => <Outlet />);

export default VaultPage;
